import { screen } from 'components/styles/variables';
import Carousel from 'nuka-carousel';
import css from 'styled-jsx/css';
import FavoriteIcon from 'components/FavoriteIcon';
import VehicleBadges from 'components/VehicleBadges';
import { Badge } from 'components/VehicleBadges/types';
import { useTranslation } from 'react-i18next';
import { Arrow, Directions } from 'components/Shared/Arrow';
import { useState, useContext, useMemo } from 'react';
import useEvents from 'lib/hooks/useEvents';
import scrollEvent, {
  Directions as ScrollDirections,
} from 'lib/Tracking/Vdp/PhotoCarousel/ScrollEvent';
import imageTapEvent from 'lib/Tracking/Vdp/PhotoCarousel/ImageTapEvent';
import favoriteToggleEvent from 'lib/Tracking/Vdp/PhotoCarousel/FavoriteToggleEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import Counter from 'components/Shared/Counter';
import loadingbox from 'public/static/images/srp/Loading_Image.svg';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { Features } from 'lib/EventSchema';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';
import ShareIcon from 'components/ShareIcon';
import navigatorShare from 'lib/navigatorShare';
import Dots from './Dots';
import LeftArrow from './left-arrow.svg';
import RightArrow from './right-arrow.svg';

interface Props {
  images: string[];
  onImageClick(): void;
  isFavorited: boolean;
  favorite(): void;
  unfavorite(): void;
  onSlideChange?: (index: number) => void;
  badges: Badge[];
  vehicleInfo?: {
    year: number;
    make: string;
    model: string;
    trim: string | null;
    vin: string;
  };
}

const sliderCss = css.resolve`
  @media (min-width: ${screen.smMin}) {
    .slider :global(.slider-frame) {
      border-radius: 16px;
    }
  }

  :global(.slider-slide) {
    outline: none;
  }

  @media (min-width: ${screen.mdMin}) {
    .slider :global(.slider-control-bottomcenter) {
      display: none;
    }
  }

  .slider :global(.slider-slide:after) {
    display: block;
    content: '';
    /* 16:10 aspect ratio */
    padding-bottom: 62.5%;
  }

  .slider :global(.slider-control-bottomcenter) {
    margin-bottom: 16px;
  }

  .image-slide {
    object-fit: cover;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  @media (max-width: ${screen.xsMax}) {
    .image-slide {
      height: 240px;
    }
  }
`;

const heartCss = css.resolve`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const counterCss = css.resolve`
  .counter {
    display: none;
    position: absolute;
    bottom: 26px;
    right: 16px;
  }

  @media (min-width: ${screen.mdMin}) {
    .counter {
      display: flex;
    }
  }
`;

export default function PhotoCarousel({
  images,
  onImageClick,
  isFavorited,
  favorite,
  unfavorite,
  onSlideChange = (): void => {},
  badges,
  vehicleInfo,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [thumbnailPage, setThumbnailPage] = useState<number>(0);

  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);
  const { isMobile } = useDeviceScreen();

  function handleMainCarouselChange(index: number): void {
    setSlideIndex(index);
    const newThumbnailPage =
      Math.floor(index / memoizedValues.thumbnailSlidesToShow) *
      memoizedValues.thumbnailSlidesToShow;
    if (newThumbnailPage !== thumbnailPage) {
      setThumbnailPage(newThumbnailPage);
    }
  }

  function logPhotoCarouselScrollEvent(direction: ScrollDirections, feature: Features): void {
    logger.log(scrollEvent({ direction, feature, trackingParams }));
  }

  const { getDecision } = useOptimizelyDecisions();
  const smsFunctionality = getDecision('smsFunctionality');

  const { year, make, model, trim, vin } = vehicleInfo || {};
  const title = `${year} ${make} ${model} ${trim}`;
  function onShareClick(): void {
    navigatorShare({
      title,
      text: 'Check out this vehicle on Autolist!',
      url: `https://www.autolist.com/listings/${vin}`,
    });
  }

  // Memoize values to prevent unnecessary recalculations
  const memoizedValues = useMemo(() => {
    const thumbnailSlidesToShowValue = isMobile ? 3 : 4;
    const mobileSlideWidthValue = smsFunctionality ? '87.274px' : '113px';
    const mobileThumbnailHeightValue = smsFunctionality ? '50px' : '64px';
    const slideWidthValue = isMobile ? mobileSlideWidthValue : '142px';
    const cellSpacingValue = smsFunctionality ? 12 : 16;
    const slidesToScrollValue = isMobile ? 1 : 4;
    const thumbnailHeightValue = isMobile ? mobileThumbnailHeightValue : '80px';
    const imageHeightValue = isMobile ? mobileThumbnailHeightValue : '80px';

    return {
      thumbnailSlidesToShow: thumbnailSlidesToShowValue,
      mobileSlideWidth: mobileSlideWidthValue,
      slideWidth: slideWidthValue,
      cellSpacing: cellSpacingValue,
      slidesToScroll: slidesToScrollValue,
      thumbnailHeight: thumbnailHeightValue,
      imageHeight: imageHeightValue,
    };
  }, [isMobile, smsFunctionality]);

  return (
    <div className="sizer">
      <div className="wrapper">
        <div className="placemat" /> {/* this provides quick-to-load LCP element for mobile */}
        {/* we need the heightMode prop from <v5 nuka-carousel to calculate the height of on page slideshow https://github.com/autolist/autolist-web/pull/4471 */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Carousel
          slideIndex={slideIndex}
          afterSlide={(index): void => {
            handleMainCarouselChange(index);
            onSlideChange(index);
          }}
          heightMode="current"
          className={sliderCss.className}
          enableKeyboardControls={true}
          wrapAround={false}
          renderBottomCenterControls={({ slideCount, currentSlide }): JSX.Element => {
            return <Dots count={slideCount} currentSlide={currentSlide} />;
          }}
          renderCenterLeftControls={({ previousSlide }): JSX.Element => (
            <Arrow
              direction={Directions.Left}
              onClick={(): void => {
                logPhotoCarouselScrollEvent('bk', Features.vehicleSnapshot);
                previousSlide();
              }}
              style={{ marginLeft: '16px' }}
              alt={t('previous')}
              data-testid="previous-slide-arrow"
            />
          )}
          renderCenterRightControls={({ nextSlide }): JSX.Element => (
            <Arrow
              alt={t('next')}
              onClick={(): void => {
                logPhotoCarouselScrollEvent('fwd', Features.vehicleSnapshot);
                nextSlide();
              }}
              style={{ marginRight: '16px' }}
              direction={Directions.Right}
            />
          )}
        >
          {images.map((image) => (
            <img
              data-testid="hero-image"
              loading="lazy"
              className={`image-slide ${sliderCss.className}`}
              src={image}
              alt="vehicle-image-slide"
              onClick={(): void => {
                logger.log(imageTapEvent({ feature: Features.vehicleSnapshot, trackingParams }));
                onImageClick();
              }}
              key={image}
            />
          ))}
        </Carousel>
        {smsFunctionality && isMobile && (
          <ShareIcon data-testid="share-icon" onClick={onShareClick} />
        )}
        <FavoriteIcon
          data-testid="heart"
          className={heartCss.className}
          active={isFavorited}
          onClick={(): void => {
            logger.log(favoriteToggleEvent({ isFavorited, trackingParams }));
            isFavorited ? unfavorite() : favorite();
          }}
        />
        <Counter
          className={`counter ${counterCss.className}`}
          count={images.length}
          index={slideIndex}
        />
        <div className="vehicle-badges">
          <VehicleBadges badges={badges} />
        </div>
      </div>
      <div className="thumbnail-carousel-wrapper">
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <Carousel
          height={memoizedValues.thumbnailHeight}
          slideWidth={memoizedValues.slideWidth}
          cellSpacing={memoizedValues.cellSpacing}
          slidesToScroll={memoizedValues.slidesToScroll}
          slideIndex={thumbnailPage}
          enableKeyboardControls={true}
          wrapAround={true}
          defaultControlsConfig={{
            pagingDotsStyle: { display: 'none' },
          }}
          renderCenterLeftControls={({ previousSlide }): JSX.Element => (
            <img
              src={LeftArrow}
              onClick={(): void => {
                logPhotoCarouselScrollEvent('bk', Features.vehicleCarousel);
                previousSlide();
              }}
              style={{
                marginLeft: '-45px',
                marginTop: '15px',
                display: isMobile ? 'none' : '',
              }}
              data-testid="thumbnail-previous-slide-arrow"
            />
          )}
          renderCenterRightControls={({ nextSlide }): JSX.Element => (
            <img
              src={RightArrow}
              onClick={(): void => {
                logPhotoCarouselScrollEvent('fwd', Features.vehicleCarousel);
                nextSlide();
              }}
              style={{
                marginRight: '-30px',
                marginTop: '15px',
                display: isMobile ? 'none' : '',
              }}
            />
          )}
        >
          {images.map((image, index) => (
            <img
              data-testid="image"
              loading="lazy"
              className={`image-slide ${slideIndex === index ? 'active-thumbnail' : ''}`}
              src={image}
              style={{
                borderRadius: 8,
                height: memoizedValues.imageHeight,
                objectFit: 'cover',
              }}
              alt="vehicle-image-slide"
              onClick={(): void => {
                logger.log(imageTapEvent({ feature: Features.vehicleCarousel, trackingParams }));
                setSlideIndex(index);
                onSlideChange(index);
              }}
              key={image}
            />
          ))}
        </Carousel>
      </div>
      {sliderCss.styles}
      {heartCss.styles}
      {counterCss.styles}
      <style jsx>{`
        .thumbnail-carousel-wrapper {
          padding: 24px 24px 8px 24px;
        }

        @media (max-width: ${screen.xsMax}) {
          .thumbnail-carousel-wrapper {
            padding: 16px 0 0 16px;
            height: ${memoizedValues.thumbnailHeight};
          }
        }

        .active-thumbnail {
          border: 2px solid #000;
        }

        .placemat {
          border-radius: 16px;
          width: 100%;
          position: absolute;
          background-image: url(${loadingbox});
        }

        @media (max-width: ${screen.xsMax}) {
          .placemat {
            border-radius: 0;
          }
        }

        .sizer {
          position: relative;
          width: 100%;
        }

        .wrapper {
          left: 0px;
          top: 0px;
          right: 0px;
          bottom: 0px;
          position: relative;
        }

        .vehicle-badges {
          position: absolute;
          bottom: 16px;
          left: 16px;
        }
      `}</style>
    </div>
  );
}
